import {AnimatePresence, m, Variants} from 'framer-motion'
import _ from 'lodash'
import {FC, useEffect, useRef} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {useTranslation} from '@/i18n'
import {MODAL_TRANSITION} from '@/theme/cssTransitions'
import {MODAL_HEADER_HEIGHT} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {useUniqueClassName} from '@/utils/hooks/useUniqueClassName'
import {MOBILE_SCREENSIZE, USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import Overlay from '../overlay/Overlay'

type ModalFullScreenProps = {
  closeModal: () => void
  isVisible: boolean
  backgroundColor?: string
  disabledButton?: boolean
  hasCloseButton?: boolean
}

const modalVariants: Variants = {
  hidden: {
    scale: 0.85,
    opacity: 0,
    transition: {duration: MODAL_TRANSITION, ease: [0, 0, 0.58, 1]},
  },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {duration: MODAL_TRANSITION, ease: [0, 0, 0.58, 1], delay: 0.16},
  },
}

export const ModalFullScreen: FC<React.PropsWithChildren<ModalFullScreenProps>> = ({
  closeModal,
  isVisible,
  backgroundColor = theme.palette.background.default,
  disabledButton = false,
  hasCloseButton = true,
  children,
}) => {
  const {t} = useTranslation()
  const modalRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (isVisible) {
      modalRef.current?.focus()
    }
  }, [isVisible])

  // disable scrolling of the page when modal is open
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflowY = 'hidden'
    } else {
      document.body.style.overflowY = 'auto'
    }
  }, [isVisible])

  const className = useUniqueClassName('motion-full-screen-modal')

  return (
    <>
      <AnimatePresence>
        {isVisible && (
          <div className={`${className}-wrapper`}>
            <Overlay close={closeModal} />
            <m.div
              className={`${className}-content`}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              onUpdate={_.noop}
            >
              <div
                className={className}
                onKeyDown={(e) => e.key === 'Escape' && closeModal()}
                role="button"
                tabIndex={0}
                ref={modalRef}
              >
                {hasCloseButton && (
                  <div className={`${className}-close-wrapper`}>
                    <div className={`${className}-close-button`}>
                      <Button
                        type="button"
                        onClick={closeModal}
                        size={ButtonSize.small}
                        variant={ButtonVariant.tertiary}
                        disabled={disabledButton}
                      >
                        {t('button-cancel')}
                      </Button>
                    </div>
                  </div>
                )}
                {isVisible && children}
              </div>
            </m.div>
          </div>
        )}
      </AnimatePresence>

      <style jsx global>{`
        .${className}-wrapper {
          width: 100%;
          height: 100%;
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 4;
          cursor: default;
        }
        .${className}-content {
          position: relative;
          display: flex;
          justify-content: center;
          margin: ${theme.spacing[3]};
          width: 100%;
          max-height: calc(100% - ${theme.spacing[3]} * 2);
          min-height: calc(100% - ${theme.spacing[3]} * 2);
          z-index: 2;
          overflow-y: auto;
        }
        .${className} {
          width: 100%;
          background-color: ${backgroundColor};
          padding: ${theme.spacing[8]} 0 ${theme.spacing[9]};
          border-radius: 12px;
          overflow-y: auto;
        }
        .${className}-close-wrapper {
          position: absolute;
          top: 0;
          right: 0;
          width: 100%;
          display: flex;
          align-items: center;
          text-align: right;
          height: ${MODAL_HEADER_HEIGHT};
          z-index: 2;
          pointer-events: none;
        }
        .${className}-close-button {
          width: 100%;
          padding-right: ${theme.spacing[2.5]};
        }
        .${className}-close-button button {
          pointer-events: auto;
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .${className} {
            padding: 0 0 ${theme.spacing[9]};
          }
          .${className}-close-wrapper {
            position: sticky;
            top: 0;
            background-color: ${backgroundColor};
            box-shadow: inset 0 -1px 0 ${theme.palette.gray[200]};
            border-top-left-radius: 12px;
            border-top-right-radius: 12px;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .${className}-content {
            margin: ${theme.spacing[2]};
            max-height: calc(100% - ${theme.spacing[2]} * 2);
            min-height: calc(100% - ${theme.spacing[2]} * 2);
          }
          .${className} {
            padding: 0 0 ${theme.spacing[8]};
          }
        }
      `}</style>
    </>
  )
}
